import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Text, toast } from 'react-common'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@platform/ui'
import classNames from 'classnames'
import { ReinforcementLearningWorkflowsApi } from '@/api/models/reinforcementLearning'
import { LEARN_EMAIL_ANSWER_SLUG } from '@/features/layout/LayoutMain/constants'
import { useSetActiveSlug } from '@/features/stencil/stubs'
import { useAppDispatch } from '@/store/hooks'
import { addBlockingOperation, removeBlockingOperation } from '@/store/slices/loadingSlice'
import ImageCorrect from './assets/correct.svg'
import ImageIncorrect from './assets/incorrect.svg'
import styles from './KMEmailResponse.module.scss'
import { CourseRedirectByActivityId } from '../courses/CourseRedirectByActivityId/CourseRedirectByActivityId'

export const KMEmailResponse: React.FC = () => {
  const [objectiveId, setObjectiveId] = useState<string>('')
  const [redirect, setRedirect] = useState(false)
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null)
  const [feedback, setFeedback] = useState<string>('')
  const [answerText, setAnswerText] = useState<string>('')
  const answerHandled = useRef(false)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useSetActiveSlug(LEARN_EMAIL_ANSWER_SLUG)

  const { t } = useTranslation()

  const { search } = useLocation()
  const params = useMemo(() => new URLSearchParams(search), [search])

  const answer = params.get('answer') as string
  const workflowId = params.get('workflowId') as string

  const title = isCorrect
    ? t('KMEmailResponse.correctWithAnswer', { answer: answerText })
    : t('KMEmailResponse.incorrectWithAnswer', { answer: answerText })

  const textRevisit = isCorrect ? t('KMEmailResponse.revisitCorrect') : t('KMEmailResponse.revisitIncorrect')

  const imgSrc = isCorrect ? ImageCorrect : ImageIncorrect
  const imgAlt = isCorrect ? t('KMEmailResponse.icon.correct') : t('KMEmailResponse.icon.incorrect')

  const redirectToObjective = useCallback(() => {
    if (!objectiveId) {
      return
    }

    setRedirect(true)
    params.set('objectiveId', objectiveId)
    navigate({ search: params.toString() }, { replace: true })
  }, [objectiveId, navigate, params])

  const onReviewModuleError = () => toast.critical(t('KMEmailResponse.moduleNotFound'))

  const fetchWorkflow = useCallback(async () => {
    try {
      const response = await ReinforcementLearningWorkflowsApi.getReinforcementLearningWorkflow(workflowId)

      if (!response) {
        return
      }
      return response
    } catch (error: any) {
      if (error.response?.status === 404) {
        toast.critical(t('KMEmailResponse.workflowNotFound'))
        navigate('/')
      }

      console.error(error)
      dispatch(removeBlockingOperation())
    }
  }, [workflowId, dispatch, t, navigate])

  const sendAnswer = useCallback(async () => {
    try {
      const response = await ReinforcementLearningWorkflowsApi.sendReinforcementLearningWorkflowAnswer(
        workflowId,
        answer,
      )
      if (!response || !response.id) {
        return
      }
      return response
    } catch (e) {
      console.error(e)
      dispatch(removeBlockingOperation())
    }
  }, [answer, workflowId, dispatch])

  const handleAnswer = useCallback(async () => {
    dispatch(addBlockingOperation())
    // Check if the answer has already been sent
    const workflow = await fetchWorkflow()

    if (!workflow) {
      return
    }

    console.log('Setting objectiveId to:', workflow.objectiveId)
    setObjectiveId(workflow.objectiveId)

    if (workflow.answer !== null) {
      dispatch(removeBlockingOperation())
      setRedirect(true)
      params.set('objectiveId', workflow.objectiveId)
      navigate({ search: params.toString() }, { replace: true })
      return
    } else {
      const answer = await sendAnswer()

      setIsCorrect(Number(answer?.normalizedScore) > 0)
      setFeedback(answer?.feedback || '')
      setAnswerText(answer?.answerText || '')
      dispatch(removeBlockingOperation())
    }
  }, [fetchWorkflow, sendAnswer, dispatch, navigate, params, setRedirect])

  useEffect(() => {
    if (!answer || !workflowId) {
      toast.critical(t('KMEmailResponse.invalidUrl'))
      return
    }

    if (answerHandled.current) {
      return
    }

    handleAnswer()
    answerHandled.current = true
  }, [handleAnswer, answer, workflowId, t])

  return (
    <div className={styles.wrapper}>
      {(!answer || !workflowId) && <Text.H3>{t('KMEmailResponse.invalidUrl')}</Text.H3>}

      {isCorrect !== null && (
        <>
          <img src={imgSrc} alt={imgAlt} />

          <div>
            <Text.H3 className={styles.title}>{title}</Text.H3>

            <Text.Body2Medium className={styles.weightNormal}>{feedback}</Text.Body2Medium>

            <Text.Body2Medium className={classNames([styles.weightNormal, styles.textRevisit])}>
              {textRevisit}
            </Text.Body2Medium>
          </div>

          <Button
            text={t('KMEmailResponse.reviewModule')}
            size="small"
            style={{ padding: '5px 16px' }}
            onClick={redirectToObjective}
          />
        </>
      )}

      {redirect && <CourseRedirectByActivityId onError={onReviewModuleError} />}
    </div>
  )
}
